import { useContext, useEffect, useMemo, useState } from 'react';
import { Grid, Typography, useTheme } from '@mui/material';
import { DataTable } from '../../components';
import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { MemberCompartments, membersStorageKey } from '../../data/members';
import { RemoveAMember } from '../../data/members/mutations';
import { MemberData } from '../../api/apis/MembersApi';
import { capitalize } from 'lodash';
import AddAMember from './AddAMember';
import EditAMember from './EditAMember';
import RemoveMemberModal from './RemoveMemberModal';
import { ReinviteMember } from '../../data/members/commands/ReinviteMember';
import { useCommands } from '../../helpers/useCommands';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import { SearchMembers } from '../../data/members/commands/SearchMembers';
import { formatDateMMDDYYYY } from '../../helpers/utilityFunctions';
import { sortAdminStatusThenDate } from '../../helpers/sortUtils';

interface MembersDataTableProps {
  searchWord?: string;
  addMemberDrawerOpen?: boolean;
  setAddMemberDrawerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onDataChange?: (filteredData: any[]) => void;
}

export const MembersDataTable: React.FC<MembersDataTableProps> = ({
  searchWord = '',
  addMemberDrawerOpen = false,
  setAddMemberDrawerOpen,
  onDataChange,
}) => {
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const commands = useCommands();
  const theme = useTheme();

  const [editMemberDrawer, setEditMemberDrawer] = useState(false);
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [activeData, setActiveData] = useState(undefined);

  const memberDataCache = appStorage.retrieve<MemberCompartments>(membersStorageKey);
  const membersData = useObservable(memberDataCache.observe$<MemberData[]>('members'));

  const removeAMember = useMutation(new RemoveAMember());
  const [filteredMembersDataForTable, setFilteredMembersDataForTable] = useState<any[]>([]);

  const headers = [
    { label: 'firstName', title: 'First Name' },
    { label: 'lastName', title: 'Last Name' },
    { label: 'email', title: 'Email' },
    { label: 'statusDisplay', title: 'Status' },
  ];

  const transformMembersData = (membersData: MemberData[]) => {
    return membersData.map(member => ({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      status: capitalize(member.status) + ' on ' + formatDateMMDDYYYY(member.statusTimestamp ?? ''),
      statusDisplay: (
        <Grid>
          <Typography variant='p16'>{capitalize(member.status)}</Typography>
          <Typography variant='body2' color='secondary'>
            {formatDateMMDDYYYY(member.statusTimestamp ?? '')}
          </Typography>
        </Grid>
      ),
      id: member.id,
    }));
  };

  const membersDataForTable = useMemo(() => {
    if (!membersData) return [];
    return transformMembersData(membersData);
  }, [membersData]);

  useEffect(() => {
    const performSearch = async () => {
      if (!searchWord || searchWord.length < 3) {
        setFilteredMembersDataForTable(membersDataForTable);
        return;
      }
      try {
        const searchResults = await commands.execute(SearchMembers, searchWord);
        setFilteredMembersDataForTable(transformMembersData(searchResults.data));
      } catch (err) {
        console.error('Error searching members:', err);
      }
    };
    performSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWord, membersDataForTable]);

  useEffect(() => {
    if (onDataChange) onDataChange(filteredMembersDataForTable);
  }, [filteredMembersDataForTable, onDataChange]);

  const handleMemberReinvite = async (row: any) => {
    try {
      await commands.execute(ReinviteMember, row.id);
      setToast(
        new Toast({
          severity: 'success',
          open: true,
          message: 'Member invite sent!',
          autoHideDuration: 3000,
        }),
      );
    } catch (err) {
      console.error('Error reinviting member', err);
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Failed to reinvite member.',
          autoHideDuration: 3000,
        }),
      );
    }
  };

  const handleRemoveMember = async (row: any) => {
    try {
      await removeAMember.action(row.id);
      setToast(
        new Toast({
          severity: 'success',
          open: true,
          message: `${row.firstName} ${row.lastName} has been removed.`,
          autoHideDuration: 3000,
        }),
      );
    } catch (err) {
      console.error(err);
      setToast(
        new Toast({
          severity: 'error',
          open: true,
          message: 'Failed to remove member.',
          autoHideDuration: 3000,
        }),
      );
    }
  };

  const onRemovePress = (row: any) => {
    setModalOpen(true);
    setActiveData(row);
  };

  const onEditPress = (row: any) => {
    setEditMemberDrawer(true);
    setSelectedMemberForEdit(row);
  };

  return (
    <>
      <RemoveMemberModal
        onSave={handleRemoveMember}
        activeData={activeData}
        open={modalOpen}
        setOpen={setModalOpen}
      />

      <DataTable
        headers={headers}
        customSort={[
          {
            column: 'statusDisplay',
            function: sortAdminStatusThenDate,
          },
        ]}
        lastColumnElement='menu'
        ellipsesItems={[
          {
            label: 'Edit',
            onClick: onEditPress,
          },
          {
            label: 'Re-invite',
            onClick: handleMemberReinvite,
            showFn: (row: any) => row.status.includes('Invited'),
          },
          {
            label: 'Remove',
            onClick: onRemovePress,
            sx: { color: theme.palette.error.dark },
            showFn: (row: any) => !row.status.includes('Removed'),
          },
        ]}
        data={filteredMembersDataForTable}
        emptyStateText="You haven't added any members yet."
        filtered={searchWord.length > 0}
      />

      {setAddMemberDrawerOpen && (
        <AddAMember drawerOpen={addMemberDrawerOpen} setDrawerOpen={setAddMemberDrawerOpen} />
      )}

      <EditAMember
        member={selectedMemberForEdit}
        drawerOpen={editMemberDrawer}
        setDrawerOpen={setEditMemberDrawer}
      />
    </>
  );
};
